import { settings as defaultSettingsRepo } from '@/lib/db';
import type { SettingsRepository } from '@/lib/db';
import type { ApiSourceDescriptor } from '@/lib/sources';
import { API_SOURCES } from '@/lib/sources';

/**
 * Where a pilot's API keys live.
 *
 * `DATA-SOURCES.md` §3's ground rules are the whole specification: *keys are
 * entered by the user, stored locally, never committed, never proxied through a
 * server we run.* This app has no server to proxy through and no account to
 * sync to, so "stored locally" means the same settings table every other
 * preference uses, and the key travels from the pilot's browser straight to the
 * source in an `Authorization` header.
 *
 * That is a deliberate trade and worth naming: IndexedDB is not a keychain.
 * Anything with script access to this origin can read a key stored here, which
 * is true of every browser-only tool that talks to an authenticated API. The
 * mitigation available today is that the key is the pilot's own, scoped to
 * their own plan, and revocable at the source; the real fix is the OS
 * keychain, which the Tauri build (`src-tauri/`, `docs/DESKTOP.md`) could
 * reach but does not yet — Phase 7 kept keys where they were and instead
 * kept them out of every backup and sync file (`lib/db/portable.ts`). The
 * settings page says so rather than implying a safety that isn't there.
 *
 * One row per field, keyed `sources.<sourceId>.<field>`, so adding a source
 * adds rows rather than a migration — the same reason `Setting` is key-value in
 * the first place.
 */

export const CREDENTIAL_KEY_PREFIX = 'sources.';

/** The settings key one credential field is stored under. */
export function credentialKey(sourceId: string, field: string): string {
  return `${CREDENTIAL_KEY_PREFIX}${sourceId}.${field}`;
}

/** Which source the logbook's "find this flight" button uses without asking. */
export const PREFERRED_SOURCE_KEY = 'sources.preferred';

/**
 * The relay every source request goes through, when the pilot has named one.
 *
 * Not a credential and not per-source, so it is its own row rather than part of
 * `sources.<id>.*`: `relay.ts` explains why one is needed at all, which is that
 * none of the three sources will talk to a page directly. `npm run dev` proxies
 * them itself and needs nothing here.
 *
 * A relay sees every request that passes through it, including the FR24
 * key in its `Authorization` header. That is the whole reason this is
 * empty by default and the field says whose relay it had better be.
 */
export const RELAY_KEY = 'sources.relay';

export function relayFrom(rows: Readonly<Record<string, unknown>>): string | undefined {
  const stored = rows[RELAY_KEY];
  if (typeof stored !== 'string' || stored.trim() === '') {
    return undefined;
  }
  return stored.trim();
}

export type SourceCredentials = Record<string, string>;

/** Every credential value for one source, as `createTrackSource` wants them. */
export function credentialsFrom(
  rows: Readonly<Record<string, unknown>>,
  sourceId: string,
): SourceCredentials {
  const prefix = credentialKey(sourceId, '');
  const credentials: SourceCredentials = {};
  for (const [key, value] of Object.entries(rows)) {
    if (key.startsWith(prefix) && typeof value === 'string' && value !== '') {
      credentials[key.slice(prefix.length)] = value;
    }
  }
  return credentials;
}

export async function readCredentials(
  sourceId: string,
  repo: SettingsRepository = defaultSettingsRepo,
): Promise<SourceCredentials> {
  return credentialsFrom(await repo.getAll(), sourceId);
}

export async function writeCredential(
  sourceId: string,
  field: string,
  value: string,
  repo: SettingsRepository = defaultSettingsRepo,
): Promise<void> {
  await repo.set(credentialKey(sourceId, field), value.trim());
}

/**
 * Whether this source can be used right now.
 *
 * "Configured" is not the same as "has a key": the ADS-B globes
 * work with nothing entered at all, and a source that needs no key is always
 * ready. Only a `required` field that is missing makes one unusable.
 */
export function isConfigured(
  descriptor: ApiSourceDescriptor,
  credentials: Readonly<SourceCredentials>,
): boolean {
  return descriptor.credentials
    .filter((field) => field.required)
    .every((field) => (credentials[field.key] ?? '').trim() !== '');
}

/** Sources ready to use, in the registry's own order. */
export function configuredSources(rows: Readonly<Record<string, unknown>>): ApiSourceDescriptor[] {
  return API_SOURCES.filter((descriptor) =>
    isConfigured(descriptor, credentialsFrom(rows, descriptor.id)),
  );
}

/**
 * The source a one-click action should use.
 *
 * The pilot's stored preference when it is still usable, otherwise the first
 * configured source — which is never nothing, because the free ones need no
 * configuring. `undefined` only if a future build ships no sources at all.
 */
export function preferredSource(
  rows: Readonly<Record<string, unknown>>,
): ApiSourceDescriptor | undefined {
  const available = configuredSources(rows);
  const stored = rows[PREFERRED_SOURCE_KEY];
  const chosen =
    typeof stored === 'string'
      ? available.find((descriptor) => descriptor.id === stored)
      : undefined;
  return chosen ?? available[0];
}

/**
 * Whether this source is pointed at a sandbox rather than at real data.
 *
 * FR24's sandbox answers every query with the same fixed sample flight — an
 * A320 from January 2023 — whatever registration and date range it was asked
 * for. That is the point of it: a key can be checked, and the whole search,
 * fetch, import and draft path walked, without spending a credit. It is also
 * the one setting in this app that makes a search return a confident,
 * well-formed, completely wrong answer, so whatever is on screen has to say
 * the sandbox is on rather than leave a pilot to notice that their Cessna has
 * become an Airbus.
 *
 * Read off the credential field rather than hard-coded to FR24: a second
 * source with a sandbox would store it under the same key.
 */
export function isSandbox(rows: Readonly<Record<string, unknown>>, sourceId: string): boolean {
  return credentialsFrom(rows, sourceId).sandbox === 'true';
}
